import React from 'react';
import { Box, Heading, Text, Alert, AlertIcon, AlertTitle, AlertDescription, VStack, Link, Divider, useColorModeValue } from '@chakra-ui/react';

const Icd10SaHpGuideline = () => {
  const sectionBg = useColorModeValue('gray.50', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  return (
    <Box maxW="900px">
      <Heading as="h1" size="xl" mb={6} color="brand.700">
        ICD-10-CM Guidelines for Surgical Assist Diagnosis Coding
      </Heading>

      <Text fontSize="lg" mb={8} color="gray.600">
        Official regulatory guidance indicates that coders and AI systems should review the entire medical record, including the History and Physical (H&P), to code diagnoses to their highest level of specificity, provided the documentation is authored by a legally accountable provider. The Operative Report is not the sole acceptable source document for extracting ICD-10-CM diagnosis codes.
      </Text>

      <Text fontSize="md" mb={8}>
        This document outlines the specific regulatory guidelines and AHA Coding Clinic citations that support synthesizing the H&P and Operative Report for Surgical Assist (SA) coding.
      </Text>

      <VStack spacing={10} align="stretch">
        
        {/* Section 1 */}
        <Box>
          <Heading as="h2" size="lg" mb={4} color="brand.800">
            1. The "Entire Record" Principle
          </Heading>
          <Text mb={4}>
            <strong>Source:</strong> <Link color="blue.500" href="https://www.cms.gov/medicare/coding-billing/icd-10-codes" isExternal>ICD-10-CM Official Guidelines for Coding and Reporting (CMS/CDC)</Link>
          </Text>
          <Text mb={4}>
            The official guidelines state that coders should review the entire medical record to determine the specific reason for the encounter.
          </Text>
          <Alert status="info" variant="left-accent" flexDirection="column" alignItems="flex-start" bg={sectionBg} borderColor="brand.500" mb={4}>
            <Box mb={2}>
              <Text fontWeight="bold">Section I.A.19 (Code Assignment and Clinical Criteria):</Text>
              <Text fontStyle="italic">"The entire record should be reviewed to determine the specific reason for the encounter and the conditions treated."</Text>
            </Box>
            <Divider my={2} borderColor={borderColor} />
            <Box>
              <Text fontWeight="bold">Section I.B.2 (Level of Detail in Coding):</Text>
              <Text fontStyle="italic">"A code is invalid if it has not been coded to the full number of characters required for that code, including the 7th character, if applicable."</Text>
              <Text fontSize="sm" mt={1} color="gray.600">(Note: If the H&P provides the detail needed to reach the highest level of specificity, it must be used to avoid assigning an invalid/truncated code).</Text>
            </Box>
          </Alert>
        </Box>

        {/* Section 2 */}
        <Box>
          <Heading as="h2" size="lg" mb={4} color="brand.800">
            2. Definition of a Legally Accountable "Provider"
          </Heading>
          <Text mb={4}>
            <strong>Source:</strong> <Link color="blue.500" href="https://www.cms.gov/medicare/coding-billing/icd-10-codes" isExternal>ICD-10-CM Official Guidelines for Coding and Reporting (CMS/CDC)</Link> (Introduction)
          </Text>
          <Text mb={4}>
            Diagnoses must be documented by a provider who is legally accountable for establishing the patient's diagnosis. The H&P is typically dictated by the surgeon or an attending physician medically clearing the patient, making it a fully valid, legally binding source.
          </Text>
          <Alert status="info" variant="left-accent" flexDirection="column" alignItems="flex-start" bg={sectionBg} borderColor="brand.500">
            <Box mb={2}>
              <Text fontWeight="bold">Introduction:</Text>
              <Text fontStyle="italic">"The term 'provider' is used throughout these guidelines to mean physician or any qualified health care practitioner who is legally accountable for establishing the patient’s diagnosis."</Text>
            </Box>
            <Divider my={2} borderColor={borderColor} />
            <Box>
              <Text fontWeight="bold">Section I.A.19:</Text>
              <Text fontStyle="italic">"The assignment of a diagnosis code is based on the provider's diagnostic statement that the condition exists. The provider's statement that the patient has a particular condition is sufficient."</Text>
            </Box>
          </Alert>
        </Box>

        {/* Section 3 */}
        <Box>
          <Heading as="h2" size="lg" mb={4} color="brand.800">
            3. Documentation from "Any Physician Involved"
          </Heading>
          <Text mb={4}>
            <strong>Source:</strong> AHA Coding Clinic (Proprietary publication, legally binding under HIPAA)
          </Text>
          <Text mb={4}>
            The American Hospital Association (AHA) Coding Clinic has explicitly ruled that coders are not restricted to just the primary surgeon's operative report.
          </Text>
          <Alert status="info" variant="left-accent" flexDirection="column" alignItems="flex-start" bg={sectionBg} borderColor="brand.500">
            <Box>
              <Text fontWeight="bold">AHA Coding Clinic, First Quarter 2014 (Page 17):</Text>
              <Text mb={2}>The AHA clarified that code assignment can be based on documentation from <strong>any physician involved in the patient's care</strong> during that episode.</Text>
              <Text fontStyle="italic">"Documentation is not limited to the face sheet, discharge summary, progress notes, history and physical, or other report(s) designed to capture diagnostic information."</Text>
            </Box>
          </Alert>
        </Box>

        {/* Section 4 */}
        <Box>
          <Heading as="h2" size="lg" mb={4} color="brand.800">
            4. Adding Specificity from Other Documents
          </Heading>
          <Text mb={4}>
            <strong>Source:</strong> AHA Coding Clinic
          </Text>
          <Text mb={4}>
            If a physician confirms a baseline diagnosis in the Operative Report (e.g., "severe stenosis"), the coder is allowed to use other documents in the record (like the H&P) to add specificity to that already confirmed diagnosis (e.g., "stenosis with neurogenic claudication").
          </Text>
          <Alert status="info" variant="left-accent" flexDirection="column" alignItems="flex-start" bg={sectionBg} borderColor="brand.500">
            <Box>
              <Text fontWeight="bold">AHA Coding Clinic, First Quarter 1999 (Page 5) & Third Quarter 2013 (Page 27):</Text>
              <Text>These issues established the principle of <strong>"Adding Specificity."</strong> Coders may utilize other physician documentation in the medical record to provide specificity to a diagnosis documented by the attending physician/surgeon, without needing to query the provider.</Text>
            </Box>
          </Alert>
        </Box>

        {/* Section 5 */}
        <Box>
          <Heading as="h2" size="lg" mb={4} color="brand.800">
            5. Non-Essential Modifiers (Parentheses in the Alphabetic Index)
          </Heading>
          <Text mb={4}>
            <strong>Source:</strong> <Link color="blue.500" href="https://www.cms.gov/medicare/coding-billing/icd-10-codes" isExternal>ICD-10-CM Official Guidelines for Coding and Reporting (CMS/CDC)</Link>
          </Text>
          <Text mb={4}>
            The official description of an ICD-10 code may contain a word that is not explicitly documented in the clinical text (e.g., <code>N73.6</code> Female pelvic peritoneal adhesions <em>(postinfective)</em>).
          </Text>
          <Text mb={4}>
            According to ICD-10-CM conventions, words enclosed in parentheses in the Alphabetic Index are <strong>non-essential modifiers</strong>.
          </Text>
          <Alert status="info" variant="left-accent" flexDirection="column" alignItems="flex-start" bg={sectionBg} borderColor="brand.500" mb={4}>
            <Box>
              <Text fontWeight="bold">Section I.A.7 (Conventions for the ICD-10-CM):</Text>
              <Text fontStyle="italic">"Parentheses are used in both the Alphabetic Index and Tabular List to enclose supplementary words that may be present or absent in the statement of a disease or procedure without affecting the code number to which it is assigned. The terms within the parentheses are referred to as nonessential modifiers."</Text>
            </Box>
          </Alert>
          <Text>
            Therefore, if a patient has documented "pelvic adhesions," the code <code>N73.6</code> is the correct default assignment, regardless of whether a past infection is explicitly documented in the Operative Report.
          </Text>
        </Box>

        {/* Conclusion */}
        <Box mt={4} p={6} bg="blue.50" borderRadius="md" border="1px solid" borderColor="blue.200">
          <Heading as="h3" size="md" mb={3} color="blue.800">
            Application to Revedy AI
          </Heading>
          <Text mb={3} color="blue.900">
            When Revedy's AI pipeline extracts a highly specific diagnosis (e.g., <code>M48.062</code> Lumbar stenosis with neurogenic claudication, or <code>M24.411</code> Recurrent dislocation) by synthesizing the Operative Report and the H&P, it is operating in compliance with ICD-10-CM and AHA Coding Clinic guidelines.
          </Text>
          <Text color="blue.900">
            Excluding the H&P and strictly coding from the Operative Report can result in assigning unspecified codes, which conflicts with the ICD-10-CM mandate to code to the highest level of known specificity. Furthermore, the AI applies ICD-10-CM indexing conventions, such as ignoring non-essential modifiers, to arrive at the default code when specific etiologies (like past infections) are not documented.
          </Text>
        </Box>

      </VStack>
    </Box>
  );
};

export default Icd10SaHpGuideline;